/**
 * Calculate trading statistics from fills data
 * 
 * A "trade" is defined as a complete position cycle: from opening a position
 * to fully closing it. This uses the fill_type field to identify when positions
 * are fully closed (full_close) or flipped (flip).
 */

import { useMemo } from "react";
import type { Fill } from "@/backend/types";
import { parseDecimal } from "@/backend/types";

export type TradingStats = {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  breakEvenTrades: number;
  winRate: number;
  totalVolume: number;
  averageTradeSize: number;
  averageProfit: number;
  averageLoss: number;
  largestWin: number;
  largestLoss: number;
  profitFactor: number;
  totalFees: number;
  averageFee: number;
};

type CompletedTrade = {
  market: string;
  totalPnl: number;
  volume: number;
  fees: number;
};

/**
 * Groups fills into complete position cycles (trades).
 * A trade is complete when fill_type is "full_close" or "flip".
 * Accumulates PnL from partial_close fills within the same position cycle.
 */
function groupFillsIntoTrades(fills: Fill[]): CompletedTrade[] {
  const completedTrades: CompletedTrade[] = [];

  // Sort fills chronologically
  const sortedFills = [...fills].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Track accumulated PnL, volume, and fees per market for current position cycle
  const currentCycle: Map<string, { pnl: number; volume: number; fees: number }> = new Map();

  for (const fill of sortedFills) {
    const pnl = parseDecimal(fill.realized_pnl);
    const size = parseDecimal(fill.size);
    const price = parseDecimal(fill.price);
    const fee = parseDecimal(fill.fee);
    const market = fill.market;
    const fillType = fill.fill_type;
    const tradeValue = size * price;

    // Get or initialize current cycle for this market
    const cycle = currentCycle.get(market) || { pnl: 0, volume: 0, fees: 0 };

    if (fillType === "open") {
      // Opening a new position - start tracking volume/fees for this cycle
      cycle.volume += tradeValue;
      cycle.fees += fee;
      currentCycle.set(market, cycle);
    } else if (fillType === "partial_close") {
      // Partial close - accumulate PnL, volume, and fees
      cycle.pnl += pnl;
      cycle.volume += tradeValue;
      cycle.fees += fee;
      currentCycle.set(market, cycle);
    } else if (fillType === "full_close" || fillType === "flip") {
      // Position fully closed or flipped - this completes the trade
      const totalPnl = cycle.pnl + pnl;
      const totalVolume = cycle.volume + tradeValue;
      const totalFees = cycle.fees + fee;

      completedTrades.push({
        market,
        totalPnl,
        volume: totalVolume,
        fees: totalFees,
      });

      // Reset cycle for this market (flip starts a new position immediately)
      currentCycle.set(market, { pnl: 0, volume: 0, fees: 0 });
    }
  }

  return completedTrades;
}

export const useTradingStats = (fills: Fill[]): TradingStats => {
  return useMemo(() => {
    if (!fills.length) {
      return {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        breakEvenTrades: 0,
        winRate: 0,
        totalVolume: 0,
        averageTradeSize: 0,
        averageProfit: 0,
        averageLoss: 0,
        largestWin: 0,
        largestLoss: 0,
        profitFactor: 0,
        totalFees: 0,
        averageFee: 0,
      };
    }

    // Group fills into completed trades (full position cycles)
    const completedTrades = groupFillsIntoTrades(fills);

    // Calculate stats from completed trades
    let winningTrades = 0;
    let losingTrades = 0;
    let breakEvenTrades = 0;
    let totalProfit = 0;
    let totalLoss = 0;
    let largestWin = 0;
    let largestLoss = 0;

    for (const trade of completedTrades) {
      if (trade.totalPnl > 0) {
        winningTrades++;
        totalProfit += trade.totalPnl;
        if (trade.totalPnl > largestWin) largestWin = trade.totalPnl;
      } else if (trade.totalPnl < 0) {
        losingTrades++;
        const loss = Math.abs(trade.totalPnl);
        totalLoss += loss;
        if (loss > largestLoss) largestLoss = loss;
      } else {
        breakEvenTrades++;
      }
    }

    // Calculate volume and fees from ALL fills (not just completed trades)
    // This gives accurate total volume/fees even for open positions
    let totalVolume = 0;
    let totalFees = 0;
    for (const fill of fills) {
      const size = parseDecimal(fill.size);
      const price = parseDecimal(fill.price);
      const fee = parseDecimal(fill.fee);
      totalVolume += size * price;
      totalFees += fee;
    }

    const totalTrades = completedTrades.length;
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
    const averageTradeSize = totalTrades > 0 ? totalVolume / totalTrades : 0;
    const averageProfit = winningTrades > 0 ? totalProfit / winningTrades : 0;
    const averageLoss = losingTrades > 0 ? totalLoss / losingTrades : 0;
    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? Infinity : 0;
    const averageFee = totalTrades > 0 ? totalFees / totalTrades : 0;

    return {
      totalTrades,
      winningTrades,
      losingTrades,
      breakEvenTrades,
      winRate,
      totalVolume,
      averageTradeSize,
      averageProfit,
      averageLoss,
      largestWin,
      largestLoss,
      profitFactor,
      totalFees,
      averageFee,
    };
  }, [fills]);
};
